import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import BulkActionsAPI from 'dashboard/api/bulkActions';

export function useContactBulkActions() {
  const { t } = useI18n();
  const selectedContactIds = ref(new Set());
  const isApplying = ref(false);
  const isFetchingAll = ref(false);

  const hasSelected = computed(() => selectedContactIds.value.size > 0);
  const selectedCount = computed(() => selectedContactIds.value.size);

  function toggleContact(id) {
    const next = new Set(selectedContactIds.value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selectedContactIds.value = next;
  }

  function selectAll(contacts) {
    selectedContactIds.value = new Set(contacts.map(c => c.id));
  }

  function clearSelection() {
    selectedContactIds.value = new Set();
  }

  async function updateLabels({ labels, mode }) {
    if (!hasSelected.value || !labels.length) return;
    isApplying.value = true;
    const isRemove = mode === 'remove';
    const successKey = isRemove
      ? 'BULK_ACTION.CONTACTS.LABELS.REMOVE_SUCCESS'
      : 'BULK_ACTION.CONTACTS.LABELS.APPLY_SUCCESS';
    const failKey = isRemove
      ? 'BULK_ACTION.CONTACTS.LABELS.REMOVE_FAILED'
      : 'BULK_ACTION.CONTACTS.LABELS.APPLY_FAILED';
    try {
      await BulkActionsAPI.create({
        type: 'Contact',
        ids: Array.from(selectedContactIds.value),
        labels: isRemove ? { remove: labels } : { add: labels },
      });
      useAlert(t(successKey));
      clearSelection();
    } catch {
      useAlert(t(failKey));
    } finally {
      isApplying.value = false;
    }
  }

  function selectAllIds(ids) {
    selectedContactIds.value = new Set(ids);
  }

  return {
    selectedContactIds,
    hasSelected,
    selectedCount,
    isApplying,
    isFetchingAll,
    toggleContact,
    selectAll,
    selectAllIds,
    clearSelection,
    updateLabels,
  };
}
